import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Plus, Sparkles, Trash2, Wand2 } from 'lucide-react';

import {
  proposeIntegration,
  saveIntegration,
  ScaffolderAiDisabledError,
  testIntegrationMetric,
  type MetricSpec,
  type ProviderSpec,
  type ScaffolderAuthKind,
  type ScaffolderCredentials,
  type TestIntegrationMetricResult,
} from '@/api/integrationScaffolder.api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * Integration scaffolder wizard (ADR-034).
 *
 * A four-step flow that turns "which integrations do you support?" into
 * "anything with a REST/JSON API":
 *
 *   1. **Describe** — base URL + auth kind + pasted API docs → AI proposes a
 *      wiring spec.
 *   2. **Review** — the proposed spec rendered as an editable form; the human
 *      fixes the auth injection point and the candidate metrics.
 *   3. **Test** — a live sample call for one metric with real credentials, so
 *      the human can see the value extract before saving.
 *   4. **Save** — name it and (optionally) connect it right away.
 *
 * Admin-only (visionary | integrator); non-admins get an access-denied card,
 * mirroring the Integrations page.
 */

type Step = 1 | 2 | 3 | 4;

const AUTH_KINDS: { value: ScaffolderAuthKind; label: string }[] = [
  { value: 'none', label: 'No auth' },
  { value: 'api_key', label: 'API key' },
  { value: 'bearer', label: 'Bearer token' },
  { value: 'basic', label: 'Basic (user + password)' },
];

const STEPS: { n: Step; label: string }[] = [
  { n: 1, label: 'Describe' },
  { n: 2, label: 'Review' },
  { n: 3, label: 'Test' },
  { n: 4, label: 'Save' },
];

const SELECT_CLASS =
  'block w-full rounded-kw-md border border-kw-border bg-white px-3.5 py-2.5 text-[14px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20';

const emptyMetric = (): MetricSpec => ({
  key: '',
  label: '',
  direction: 'higher_is_better',
  method: 'GET',
  path: '/',
  valuePath: '',
});

/** A blank spec to hand-edit when AI is unavailable. */
const starterSpec = (authKind: ScaffolderAuthKind): ProviderSpec => ({
  auth: {
    in: authKind === 'none' ? 'query' : 'header',
    name: authKind === 'none' ? 'api_key' : 'Authorization',
    ...(authKind === 'bearer' ? { scheme: 'Bearer' } : {}),
  },
  metrics: [emptyMetric()],
});

export function IntegrationScaffolderPage() {
  const { state: authState } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const canManage =
    authState.status === 'authenticated' &&
    ['visionary', 'integrator'].includes(authState.me.membership.role);

  const [step, setStep] = useState<Step>(1);

  // Step 1 — Describe
  const [baseUrl, setBaseUrl] = useState('');
  const [authKind, setAuthKind] = useState<ScaffolderAuthKind>('api_key');
  const [category, setCategory] = useState('custom');
  const [docsText, setDocsText] = useState('');
  const [aiDisabled, setAiDisabled] = useState(false);
  const [proposeError, setProposeError] = useState<string | null>(null);

  // Step 2 — Review (editable spec)
  const [spec, setSpec] = useState<ProviderSpec | null>(null);

  // Step 3 — Test
  const [apiKey, setApiKey] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [testMetricKey, setTestMetricKey] = useState('');
  const [testResult, setTestResult] = useState<TestIntegrationMetricResult | null>(null);

  // Step 4 — Save
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [connectNow, setConnectNow] = useState(false);

  function currentCredentials(): ScaffolderCredentials {
    if (authKind === 'basic') return { username: username.trim(), password };
    if (authKind === 'api_key' || authKind === 'bearer') return { apiKey: apiKey.trim() };
    return {};
  }

  const proposeMutation = useMutation({
    mutationFn: () =>
      proposeIntegration({
        docsText: docsText.trim(),
        baseUrl: baseUrl.trim(),
        category: category.trim() || undefined,
        authKind,
      }),
    onSuccess: (proposed) => {
      setSpec(proposed);
      setAiDisabled(false);
      setProposeError(null);
      setTestMetricKey(proposed.metrics[0]?.key ?? '');
      setStep(2);
    },
    onError: (err) => {
      if (err instanceof ScaffolderAiDisabledError) {
        setAiDisabled(true);
        setProposeError(null);
        return;
      }
      setProposeError(describeProposeError(err));
    },
  });

  const testMutation = useMutation({
    mutationFn: () => {
      if (!spec) return Promise.reject(new Error('No spec to test'));
      return testIntegrationMetric({
        spec,
        metricKey: testMetricKey,
        baseUrl: baseUrl.trim(),
        authKind,
        credentials: currentCredentials(),
      });
    },
    onSuccess: (result) => {
      setTestResult(result);
    },
    onError: (err) => {
      setTestResult(null);
      toast.show(describeTestError(err), { variant: 'danger' });
    },
  });

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!spec) return Promise.reject(new Error('No spec to save'));
      return saveIntegration({
        name: name.trim(),
        displayName: displayName.trim(),
        category: category.trim() || 'custom',
        baseUrl: baseUrl.trim(),
        authKind,
        spec,
        ...(connectNow ? { connect: { credentials: currentCredentials() } } : {}),
      });
    },
    onSuccess: (result) => {
      toast.show(
        result.integrationId
          ? `Saved and connected "${result.provider.displayName}".`
          : `Saved "${result.provider.displayName}". Connect it from Integrations when you're ready.`,
        { variant: 'success' },
      );
      void navigate('/settings/integrations');
    },
    onError: (err) => {
      toast.show(describeSaveError(err), { variant: 'danger' });
    },
  });

  if (!canManage) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <Link
          to="/settings/integrations"
          className="inline-flex items-center gap-1 text-[13px] text-kw-muted hover:text-kw-shark"
        >
          <ArrowLeft className="size-4" />
          Back to integrations
        </Link>
        <Card className="flex items-start gap-3 p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-warning-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-shark">Admin access required</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              Building integrations is managed by visionary and integrator roles.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // ── Step gating ────────────────────────────────────────────────────
  const canPropose = baseUrl.trim() !== '' && docsText.trim() !== '';
  const credsReady =
    authKind === 'none' ||
    (authKind === 'basic' ? username.trim() !== '' && password !== '' : apiKey.trim() !== '');
  const canSave = name.trim() !== '' && displayName.trim() !== '' && (!connectNow || credsReady);

  function updateSpec(next: ProviderSpec): void {
    setSpec(next);
  }

  function updateMetric(index: number, patch: Partial<MetricSpec>): void {
    if (!spec) return;
    const metrics = spec.metrics.map((m, i) => (i === index ? { ...m, ...patch } : m));
    updateSpec({ ...spec, metrics });
  }

  function addMetric(): void {
    if (!spec) return;
    updateSpec({ ...spec, metrics: [...spec.metrics, emptyMetric()] });
  }

  function removeMetric(index: number): void {
    if (!spec) return;
    const metrics = spec.metrics.filter((_, i) => i !== index);
    updateSpec({ ...spec, metrics });
    // Keep the test picker pointed at a metric that still exists.
    if (spec.metrics[index]?.key === testMetricKey) {
      setTestMetricKey(metrics[0]?.key ?? '');
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <Link
          to="/settings/integrations"
          className="inline-flex items-center gap-1 text-[12px] text-kw-muted hover:text-kw-shark"
        >
          <ArrowLeft className="size-3.5" />
          Back to integrations
        </Link>
        <h1 className="typo-h1 mt-1">Build a custom integration</h1>
        <p className="mt-1 text-[14px] text-kw-muted">
          Connect any system with a REST/JSON API. Paste its docs, let AI propose the wiring, run a live
          sample call, then save it as a provider your scorecards can pull from.
        </p>
      </div>

      <StepIndicator current={step} />

      {step === 1 ? (
        <DescribeStep
          baseUrl={baseUrl}
          onBaseUrl={setBaseUrl}
          authKind={authKind}
          onAuthKind={setAuthKind}
          category={category}
          onCategory={setCategory}
          docsText={docsText}
          onDocsText={setDocsText}
          canPropose={canPropose}
          isProposing={proposeMutation.isPending}
          aiDisabled={aiDisabled}
          proposeError={proposeError}
          onPropose={() => {
            proposeMutation.mutate();
          }}
          onManual={() => {
            const seeded = starterSpec(authKind);
            setSpec(seeded);
            setTestMetricKey(seeded.metrics[0]?.key ?? '');
            setStep(2);
          }}
        />
      ) : null}

      {step === 2 && spec ? (
        <ReviewStep
          spec={spec}
          onAuthChange={(auth) => {
            updateSpec({ ...spec, auth });
          }}
          onMetricChange={updateMetric}
          onAddMetric={addMetric}
          onRemoveMetric={removeMetric}
          onBack={() => {
            setStep(1);
          }}
          onNext={() => {
            if (!spec.metrics.some((m) => m.key === testMetricKey)) {
              setTestMetricKey(spec.metrics[0]?.key ?? '');
            }
            setStep(3);
          }}
        />
      ) : null}

      {step === 3 && spec ? (
        <TestStep
          spec={spec}
          authKind={authKind}
          apiKey={apiKey}
          onApiKey={setApiKey}
          username={username}
          onUsername={setUsername}
          password={password}
          onPassword={setPassword}
          metricKey={testMetricKey}
          onMetricKey={setTestMetricKey}
          credsReady={credsReady}
          isTesting={testMutation.isPending}
          result={testResult}
          onRunTest={() => {
            testMutation.mutate();
          }}
          onBack={() => {
            setStep(2);
          }}
          onNext={() => {
            setStep(4);
          }}
        />
      ) : null}

      {step === 4 && spec ? (
        <SaveStep
          name={name}
          onName={setName}
          displayName={displayName}
          onDisplayName={setDisplayName}
          connectNow={connectNow}
          onConnectNow={setConnectNow}
          credsReady={credsReady}
          canSave={canSave}
          isSaving={saveMutation.isPending}
          onBack={() => {
            setStep(3);
          }}
          onSave={() => {
            saveMutation.mutate();
          }}
        />
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Step indicator
// ─────────────────────────────────────────────────────────────────────────

function StepIndicator({ current }: { current: Step }) {
  return (
    <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
      {STEPS.map((s, i) => {
        const active = s.n === current;
        const done = s.n < current;
        return (
          <li key={s.n} className="flex items-center gap-2">
            <span
              className={
                active
                  ? 'inline-flex items-center gap-1.5 rounded-kw-full bg-kw-cerulean-light px-2.5 py-1 font-medium text-kw-cerulean-dark'
                  : done
                    ? 'inline-flex items-center gap-1.5 rounded-kw-full bg-kw-emerald-light px-2.5 py-1 font-medium text-kw-tag-text'
                    : 'inline-flex items-center gap-1.5 rounded-kw-full bg-kw-bg px-2.5 py-1 text-kw-muted'
              }
            >
              <span className="tabular-nums">{s.n}</span>
              {s.label}
            </span>
            {i < STEPS.length - 1 ? <span className="text-kw-ghost">→</span> : null}
          </li>
        );
      })}
    </ol>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Step 1 — Describe
// ─────────────────────────────────────────────────────────────────────────

function DescribeStep({
  baseUrl,
  onBaseUrl,
  authKind,
  onAuthKind,
  category,
  onCategory,
  docsText,
  onDocsText,
  canPropose,
  isProposing,
  aiDisabled,
  proposeError,
  onPropose,
  onManual,
}: {
  baseUrl: string;
  onBaseUrl: (v: string) => void;
  authKind: ScaffolderAuthKind;
  onAuthKind: (v: ScaffolderAuthKind) => void;
  category: string;
  onCategory: (v: string) => void;
  docsText: string;
  onDocsText: (v: string) => void;
  canPropose: boolean;
  isProposing: boolean;
  aiDisabled: boolean;
  proposeError: string | null;
  onPropose: () => void;
  onManual: () => void;
}) {
  return (
    <Card className="space-y-4 p-5">
      <div>
        <h2 className="text-[15px] font-medium text-kw-shark">Describe the API</h2>
        <p className="mt-0.5 text-[13px] text-kw-muted">
          Tell us where the API lives and how it authenticates, then paste the relevant docs.
        </p>
      </div>

      <Input
        label="Base URL"
        name="baseUrl"
        type="url"
        autoComplete="off"
        placeholder="https://api.example.com"
        value={baseUrl}
        onChange={(e) => {
          onBaseUrl(e.target.value);
        }}
        hint="Must be an https origin. Requests are made server-side with SSRF protection."
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="space-y-1.5">
          <span className="block text-[13px] font-medium text-kw-label">Authentication</span>
          <select
            className={SELECT_CLASS}
            value={authKind}
            onChange={(e) => {
              onAuthKind(e.target.value as ScaffolderAuthKind);
            }}
          >
            {AUTH_KINDS.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </select>
        </label>

        <Input
          label="Category"
          name="category"
          autoComplete="off"
          placeholder="custom"
          value={category}
          onChange={(e) => {
            onCategory(e.target.value);
          }}
          hint="A short label for grouping, e.g. fulfillment or finance."
        />
      </div>

      <label className="space-y-1.5">
        <span className="block text-[13px] font-medium text-kw-label">API documentation</span>
        <textarea
          rows={10}
          placeholder="Paste the endpoint reference: URLs, auth header, and an example JSON response…"
          value={docsText}
          onChange={(e) => {
            onDocsText(e.target.value);
          }}
          className="block w-full rounded-kw-md border border-kw-border bg-white px-3.5 py-2.5 font-mono text-[13px] text-kw-shark placeholder:font-sans placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
        />
        <span className="text-[12px] text-kw-muted">
          The docs are sent to AI to propose the wiring — never include credentials here.
        </span>
      </label>

      {aiDisabled ? (
        <div className="flex items-start gap-2 rounded-kw-md border border-kw-border bg-kw-bg p-3">
          <Sparkles className="mt-0.5 size-4 shrink-0 text-kw-muted" />
          <div>
            <p className="text-[13px] font-medium text-kw-shark">AI isn&apos;t configured</p>
            <p className="mt-0.5 text-[12px] text-kw-muted">
              Automatic proposals need an AI key set up for your organization. You can still build a provider
              by hand — continue and fill in the spec on the next step.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button size="sm" variant="secondary" type="button" onClick={onManual}>
                Continue without AI
              </Button>
              <Button size="sm" variant="ghost" type="button" onClick={onPropose}>
                Try again
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {proposeError ? (
        <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
          <p className="text-[12px] text-kw-danger-text">{proposeError}</p>
        </div>
      ) : null}

      <div className="flex justify-end">
        <Button onClick={onPropose} isLoading={isProposing} disabled={!canPropose}>
          <Wand2 className="size-4" />
          Propose with AI
        </Button>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Step 2 — Review (editable spec)
// ─────────────────────────────────────────────────────────────────────────

function ReviewStep({
  spec,
  onAuthChange,
  onMetricChange,
  onAddMetric,
  onRemoveMetric,
  onBack,
  onNext,
}: {
  spec: ProviderSpec;
  onAuthChange: (auth: ProviderSpec['auth']) => void;
  onMetricChange: (index: number, patch: Partial<MetricSpec>) => void;
  onAddMetric: () => void;
  onRemoveMetric: (index: number) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const metricsValid =
    spec.metrics.length > 0 &&
    spec.metrics.every(
      (m) =>
        m.key.trim() !== '' && m.label.trim() !== '' && m.path.trim() !== '' && m.valuePath.trim() !== '',
    );

  return (
    <Card className="space-y-5 p-5">
      <div>
        <h2 className="text-[15px] font-medium text-kw-shark">Review the proposed spec</h2>
        <p className="mt-0.5 text-[13px] text-kw-muted">
          AI drafted this from your docs — edit anything before testing. Use{' '}
          <code className="rounded bg-kw-bg px-1 py-0.5 font-mono text-[12px]">{'{weekStart}'}</code> and{' '}
          <code className="rounded bg-kw-bg px-1 py-0.5 font-mono text-[12px]">{'{weekEnd}'}</code> in a path
          for time-windowed endpoints.
        </p>
      </div>

      {/* Auth injection */}
      <section className="space-y-3">
        <h3 className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">
          Credential placement
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <label className="space-y-1.5">
            <span className="block text-[13px] font-medium text-kw-label">Inject in</span>
            <select
              className={SELECT_CLASS}
              value={spec.auth.in}
              onChange={(e) => {
                onAuthChange({ ...spec.auth, in: e.target.value as 'header' | 'query' });
              }}
            >
              <option value="header">Header</option>
              <option value="query">Query param</option>
            </select>
          </label>
          <Input
            label="Name"
            name="authName"
            autoComplete="off"
            placeholder={spec.auth.in === 'header' ? 'Authorization' : 'api_key'}
            value={spec.auth.name}
            onChange={(e) => {
              onAuthChange({ ...spec.auth, name: e.target.value });
            }}
          />
          <Input
            label="Scheme (optional)"
            name="authScheme"
            autoComplete="off"
            placeholder="Bearer"
            value={spec.auth.scheme ?? ''}
            onChange={(e) => {
              const scheme = e.target.value;
              onAuthChange({ ...spec.auth, ...(scheme.trim() === '' ? { scheme: undefined } : { scheme }) });
            }}
          />
        </div>
      </section>

      {/* Metrics */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">
            Metrics <span className="font-normal text-kw-ghost">({spec.metrics.length})</span>
          </h3>
          <Button size="sm" variant="secondary" onClick={onAddMetric}>
            <Plus className="size-4" />
            Add metric
          </Button>
        </div>

        {spec.metrics.length === 0 ? (
          <p className="rounded-kw-md border border-dashed border-kw-border p-4 text-center text-[13px] text-kw-muted">
            No metrics yet. Add at least one to pull a number from the API.
          </p>
        ) : (
          <ul className="space-y-3">
            {spec.metrics.map((m, i) => (
              <li key={i} className="rounded-kw-md border border-kw-border p-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[12px] font-medium text-kw-muted">Metric {i + 1}</span>
                  <button
                    type="button"
                    aria-label={`Remove metric ${String(i + 1)}`}
                    onClick={() => {
                      onRemoveMetric(i);
                    }}
                    className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Input
                    label="Key"
                    name={`metric-${String(i)}-key`}
                    autoComplete="off"
                    placeholder="orders_shipped"
                    value={m.key}
                    onChange={(e) => {
                      onMetricChange(i, { key: e.target.value });
                    }}
                  />
                  <Input
                    label="Label"
                    name={`metric-${String(i)}-label`}
                    autoComplete="off"
                    placeholder="Orders shipped"
                    value={m.label}
                    onChange={(e) => {
                      onMetricChange(i, { label: e.target.value });
                    }}
                  />
                  <label className="space-y-1.5">
                    <span className="block text-[13px] font-medium text-kw-label">Direction</span>
                    <select
                      className={SELECT_CLASS}
                      value={m.direction}
                      onChange={(e) => {
                        onMetricChange(i, {
                          direction: e.target.value as MetricSpec['direction'],
                        });
                      }}
                    >
                      <option value="higher_is_better">Higher is better</option>
                      <option value="lower_is_better">Lower is better</option>
                    </select>
                  </label>
                  <Input
                    label="Path"
                    name={`metric-${String(i)}-path`}
                    autoComplete="off"
                    placeholder="/v1/reports/orders"
                    value={m.path}
                    onChange={(e) => {
                      onMetricChange(i, { path: e.target.value });
                    }}
                  />
                  <div className="sm:col-span-2">
                    <Input
                      label="Value path"
                      name={`metric-${String(i)}-valuePath`}
                      autoComplete="off"
                      placeholder="data.totals.shipped"
                      value={m.valuePath}
                      onChange={(e) => {
                        onMetricChange(i, { valuePath: e.target.value });
                      }}
                      hint="Dotted path to the number in the JSON response (array indices allowed, e.g. items.0.value)."
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onNext} disabled={!metricsValid}>
          Next: test it
        </Button>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Step 3 — Test
// ─────────────────────────────────────────────────────────────────────────

function TestStep({
  spec,
  authKind,
  apiKey,
  onApiKey,
  username,
  onUsername,
  password,
  onPassword,
  metricKey,
  onMetricKey,
  credsReady,
  isTesting,
  result,
  onRunTest,
  onBack,
  onNext,
}: {
  spec: ProviderSpec;
  authKind: ScaffolderAuthKind;
  apiKey: string;
  onApiKey: (v: string) => void;
  username: string;
  onUsername: (v: string) => void;
  password: string;
  onPassword: (v: string) => void;
  metricKey: string;
  onMetricKey: (v: string) => void;
  credsReady: boolean;
  isTesting: boolean;
  result: TestIntegrationMetricResult | null;
  onRunTest: () => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const ok = result !== null && result.status >= 200 && result.status < 300 && result.value !== null;

  return (
    <Card className="space-y-4 p-5">
      <div>
        <h2 className="text-[15px] font-medium text-kw-shark">Run a live test</h2>
        <p className="mt-0.5 text-[13px] text-kw-muted">
          Pick a metric and enter credentials to pull one real value.
        </p>
      </div>

      <div className="flex items-start gap-2 rounded-kw-md border border-kw-warning-text/30 bg-kw-warning-bg p-3">
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-warning-text" />
        <p className="text-[12px] text-kw-warning-text">
          This makes a real request to the external API using the credentials you enter below.
        </p>
      </div>

      {authKind === 'none' ? (
        <p className="text-[13px] text-kw-muted">This provider needs no credentials.</p>
      ) : authKind === 'basic' ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Username"
            name="testUsername"
            autoComplete="off"
            value={username}
            onChange={(e) => {
              onUsername(e.target.value);
            }}
          />
          <Input
            label="Password"
            name="testPassword"
            type="password"
            autoComplete="off"
            value={password}
            onChange={(e) => {
              onPassword(e.target.value);
            }}
          />
        </div>
      ) : (
        <Input
          label={authKind === 'bearer' ? 'Bearer token' : 'API key'}
          name="testApiKey"
          type="password"
          autoComplete="off"
          value={apiKey}
          onChange={(e) => {
            onApiKey(e.target.value);
          }}
          hint="Used only for this test unless you choose to store it on the Save step."
        />
      )}

      <label className="space-y-1.5">
        <span className="block text-[13px] font-medium text-kw-label">Metric to test</span>
        <select
          className={SELECT_CLASS}
          value={metricKey}
          onChange={(e) => {
            onMetricKey(e.target.value);
          }}
        >
          {spec.metrics.map((m) => (
            <option key={m.key} value={m.key}>
              {m.label || m.key}
            </option>
          ))}
        </select>
      </label>

      <div className="flex justify-end">
        <Button onClick={onRunTest} isLoading={isTesting} disabled={!credsReady || metricKey === ''}>
          Run live test
        </Button>
      </div>

      {result ? (
        <div
          className={
            ok
              ? 'rounded-kw-md border border-kw-emerald/30 bg-kw-emerald-light p-4'
              : 'rounded-kw-md border border-kw-border bg-kw-bg p-4'
          }
        >
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">
              HTTP {result.status}
            </span>
            {result.value !== null ? (
              <span className="text-[15px] font-semibold text-kw-shark">
                Value: <span className="tabular-nums">{result.value}</span>
              </span>
            ) : (
              <span className="text-[13px] font-medium text-kw-danger-text">
                Couldn&apos;t extract a value
              </span>
            )}
          </div>
          {result.extractError ? (
            <p className="mt-2 text-[12px] text-kw-danger-text">{result.extractError}</p>
          ) : null}
          {result.value === null ? (
            <>
              <p className="mt-3 text-[12px] text-kw-muted">
                Response preview — go back and fix the value path if the number is elsewhere:
              </p>
              <pre className="mt-1 max-h-64 overflow-auto rounded-kw-md border border-kw-border bg-white p-3 font-mono text-[12px] text-kw-shark">
                {result.responsePreview}
              </pre>
            </>
          ) : null}
        </div>
      ) : null}

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onNext}>Next: save</Button>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Step 4 — Save
// ─────────────────────────────────────────────────────────────────────────

function SaveStep({
  name,
  onName,
  displayName,
  onDisplayName,
  connectNow,
  onConnectNow,
  credsReady,
  canSave,
  isSaving,
  onBack,
  onSave,
}: {
  name: string;
  onName: (v: string) => void;
  displayName: string;
  onDisplayName: (v: string) => void;
  connectNow: boolean;
  onConnectNow: (v: boolean) => void;
  credsReady: boolean;
  canSave: boolean;
  isSaving: boolean;
  onBack: () => void;
  onSave: () => void;
}) {
  return (
    <Card className="space-y-4 p-5">
      <div>
        <h2 className="text-[15px] font-medium text-kw-shark">Name and save</h2>
        <p className="mt-0.5 text-[13px] text-kw-muted">
          Give the provider a stable machine name and a friendly display name.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label="Machine name"
          name="providerName"
          autoComplete="off"
          placeholder="acme_orders"
          value={name}
          onChange={(e) => {
            onName(e.target.value);
          }}
          hint="Lowercase slug, unique within your organization."
        />
        <Input
          label="Display name"
          name="providerDisplayName"
          autoComplete="off"
          placeholder="Acme Orders"
          value={displayName}
          onChange={(e) => {
            onDisplayName(e.target.value);
          }}
        />
      </div>

      <label className="flex items-start gap-3 rounded-kw-md border border-kw-border p-3">
        <input
          type="checkbox"
          checked={connectNow}
          onChange={(e) => {
            onConnectNow(e.target.checked);
          }}
          className="mt-0.5 size-4 rounded border-kw-border text-kw-cerulean focus:ring-kw-cerulean/30"
        />
        <span>
          <span className="block text-[13px] font-medium text-kw-shark">Connect now</span>
          <span className="block text-[12px] text-kw-muted">
            Store the credentials you entered and start syncing immediately. Otherwise the provider is saved
            as a draft you can connect later.
          </span>
        </span>
      </label>

      {connectNow && !credsReady ? (
        <div className="flex items-start gap-2 rounded-kw-md border border-kw-warning-text/30 bg-kw-warning-bg p-3">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-warning-text" />
          <p className="text-[12px] text-kw-warning-text">
            Go back to the Test step and enter credentials to connect now.
          </p>
        </div>
      ) : null}

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onSave} isLoading={isSaving} disabled={!canSave}>
          {connectNow ? 'Save & connect' : 'Save provider'}
        </Button>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Error mapping
// ─────────────────────────────────────────────────────────────────────────

function errorCode(err: unknown): { code?: unknown; message?: unknown; status?: number } {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (
      err as {
        response?: { status?: number; data?: { error?: unknown; message?: unknown } };
      }
    ).response;
    return { code: response?.data?.error, message: response?.data?.message, status: response?.status };
  }
  return {};
}

function describeProposeError(err: unknown): string {
  const { code, message } = errorCode(err);
  if (code === 'ai_could_not_propose')
    return "AI couldn't turn these docs into a spec. Try pasting a clearer endpoint reference, or build the spec by hand.";
  if (typeof message === 'string' && message !== '') return message;
  return 'The proposal request failed. Please try again.';
}

function describeTestError(err: unknown): string {
  const { code, message } = errorCode(err);
  if (code === 'unsafe_url')
    return typeof message === 'string' && message !== ''
      ? message
      : 'That URL points at a blocked address and cannot be called.';
  if (typeof message === 'string' && message !== '') return message;
  return err instanceof Error ? err.message : 'The test call failed.';
}

function describeSaveError(err: unknown): string {
  const { code, status, message } = errorCode(err);
  if (status === 409 || code === 'name_taken')
    return 'A provider with that machine name already exists. Pick a different name.';
  if (typeof message === 'string' && message !== '') return message;
  return err instanceof Error ? err.message : 'Saving failed. Please try again.';
}
